// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { XMarkIcon, PencilSquareIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { updateAssetReturn, deleteAssetReturn } from '../../viewServices/assets_api';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import ModalConfirm from 'views/finance/moduleComponents/ModalConfirm';
import { toDatePart } from 'shared_items/formatConventions';

// ###### HELPERS
const parseDecimal = (v) => {
  if (v === '' || v == null) return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

const PAYMENT_TYPES = [
  { value: 'DIVIDENDOS', label: 'Dividendos' },
  { value: 'JURO CAPITAL PROPRIO', label: 'Juro Capital Próprio' },
  { value: 'REND IMOBILIARIO', label: 'Rendimento Imobiliário' },
  { value: 'other', label: 'Other' },
];

// ###### COMPONENT
const ModalReturnEdit = ({ selectedReturn, onClose, onSuccess }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [edited, setEdited] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (selectedReturn) {
      setEdited({
        payment_type: selectedReturn.payment_type || 'DIVIDENDOS',
        // Date-only field ("YYYY-MM-DD"): keep as string, never parse via new Date() (UTC shift)
        payment_date: toDatePart(selectedReturn.payment_date),
        payment_value: selectedReturn.payment_value != null ? String(selectedReturn.payment_value) : '',
        quantity_at_time: selectedReturn.quantity_at_time != null ? String(selectedReturn.quantity_at_time) : '',
        notes: selectedReturn.notes || '',
      });
      setIsEditing(false);
    }
  }, [selectedReturn]);

  if (!selectedReturn || !edited) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEdited((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!edited.payment_date?.trim()) {
      toast.error('Data do pagamento é obrigatória.');
      return;
    }
    const paymentVal = parseDecimal(edited.payment_value);
    if (paymentVal === null) {
      toast.error('Valor do pagamento é obrigatório e deve ser um número válido.');
      return;
    }
    const qtyVal = parseDecimal(edited.quantity_at_time);
    if (edited.quantity_at_time?.trim() !== '' && qtyVal === null) {
      toast.error('Quantidade inválida.');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        payment_type: edited.payment_type,
        payment_date: edited.payment_date,
        payment_value: paymentVal,
        quantity_at_time: qtyVal,
        notes: edited.notes?.trim() || null,
      };
      await updateAssetReturn(selectedReturn.id, payload);
      toast.success('Return atualizado.');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erro ao atualizar return.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReturn = async () => {
    if (!selectedReturn?.id) return;
    setShowDeleteConfirm(false);
    try {
      setDeleting(true);
      await deleteAssetReturn(selectedReturn.id);
      toast.success('Return excluído.');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erro ao excluir return.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-4 md:p-6 rounded-2xl shadow w-80 md:w-96 relative max-h-[90vh] overflow-y-auto">
        {(submitting || deleting) && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/90 rounded-2xl">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2 flex gap-2">
          {!isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={submitting || deleting}
              className={`text-gray-500 ${submitting || deleting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
              title="Editar"
            >
              <PencilSquareIcon className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={submitting || deleting}
            className={`text-gray-500 ${submitting || deleting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
            title="Fechar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <h2 className="text-lg font-semibold mb-4 pr-10">
          {isEditing ? 'Editar return' : 'Detalhes do return'}
        </h2>

        <form className="space-y-4" onSubmit={handleUpdate}>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Asset</label>
            <div className="px-2 py-1.5 border rounded-md text-sm bg-gray-50 text-gray-600">
              <span className="font-medium text-gray-800">{selectedReturn.asset_name ?? '—'}</span>
              <span className="ml-2 text-xs font-mono text-gray-500">{selectedReturn.asset_code ?? '—'}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Tipo de pagamento</label>
            <select
              name="payment_type"
              value={edited.payment_type}
              onChange={handleInputChange}
              className="w-full px-2 py-1.5 border rounded-md text-sm bg-white disabled:bg-gray-50 disabled:text-gray-600"
              disabled={!isEditing || submitting || deleting}
            >
              {PAYMENT_TYPES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Data do pagamento</label>
            <input
              type="date"
              name="payment_date"
              value={edited.payment_date}
              onChange={handleInputChange}
              className="w-full px-2 py-1.5 border rounded-md text-sm"
              disabled={!isEditing || submitting || deleting}
              required
            />
          </div>

          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-gray-500 mb-0.5">Valor (R$)</label>
              <input
                type="text"
                name="payment_value"
                value={edited.payment_value}
                onChange={handleInputChange}
                placeholder="0,00"
                className="w-full px-2 py-1.5 border rounded-md text-sm tabular-nums"
                disabled={!isEditing || submitting || deleting}
                inputMode="decimal"
                required
              />
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-gray-500 mb-0.5">Quantidade na data (opcional)</label>
              <input
                type="text"
                name="quantity_at_time"
                value={edited.quantity_at_time}
                onChange={handleInputChange}
                placeholder="Ex: 100"
                className="w-full px-2 py-1.5 border rounded-md text-sm tabular-nums"
                disabled={!isEditing || submitting || deleting}
                inputMode="decimal"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Observações (opcional)</label>
            <textarea
              name="notes"
              value={edited.notes}
              onChange={handleInputChange}
              placeholder="Notas"
              rows={2}
              className="w-full px-2 py-1.5 border rounded-md text-sm resize-none"
              disabled={!isEditing || submitting || deleting}
            />
          </div>

          {isEditing && (
            <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={submitting || deleting}
                className="shrink-0 rounded-xl border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Excluir return
              </button>
              <div className="ml-auto flex flex-1 flex-wrap justify-end gap-2 min-w-0">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting || deleting}
                  className="px-4 h-10 rounded-2xl border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting || deleting}
                  className={`min-w-[7rem] h-10 rounded-2xl px-4 text-white ${submitting || deleting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {submitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>

    <ModalConfirm
      isOpen={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      onConfirm={handleDeleteReturn}
      title="Excluir return"
      message="Este return será removido permanentemente. Não é possível desfazer."
      confirmText="Excluir"
      cancelText="Cancelar"
      disabled={deleting}
      confirmButtonClass="bg-red-600 hover:bg-red-700 text-white shadow"
    />
    </>
  );
};

export default ModalReturnEdit;
